import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { ConfigService } from '../services/config.service';
import { AdminService } from '../admin-pages/admin.service';
import { ThirdPartyService } from '../third-party/third-party.service';
import { LogglyService } from 'src/app/services/loggly.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  errorStatus = [401, 403];
  noAuthAPI = [
    '/Authentication/AdminLogin',
    'Authentication/RefreshToken',
    '/User/GenerateOTPForAdminUser',
    '/User/ResetAdminPasswordUsingOTP',
    '/Seller/InsertIntoSellerEnqueryList',
    '/Seller/CheckSubdomainExist',
    '/Seller/SuperAdminCreateSellerWithMinParam',
    '/Order/GenerateOTP',
    '/Order/CompareOTP',
    '/Order/CompareOTPWithStringReturn',
    'GetSellerDetailsBySubDomainForAdmin',
    '/Order/GetRegistrationFlowStatusByPhoneNumber',
    '/Seller/InsertSellerSubdominWithRegistrationStatus',
    '/Seller/GetTermsAndConditionsForSellerBySellerId',
    '/Public/GetTermsAndConditionsForSellerBySellerId',
    '/Seller/SellerAcceptTermsAndCondition',
    '/Seller/SuperAdminCreateSellerWithMinParamVTwo',
    'Seller/GetFeatureFlags',
    '/Payment/SaveSellerPaymentGatewayKeys',
    '.json',
    'Seller/GetMenuCardTables',
    '/Public/SuperAdminCreateSellerWithMinDetails',
    '/Seller/SendRegistrationWhatsAppOTP', // ServiceNear sign-up, before login
    '/Servicenear/UserExistsCheck',
    '/Servicenear/NewCompanyRegistration',
    '/Servicenear/GetServicenearTempKeyForLogin',
  ];

  constructor(private configService: ConfigService, private authorizedService: AdminService , private thirdPartyService:ThirdPartyService,private logglyService: LogglyService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let authReq = request;

    const sellerUuid = localStorage.getItem('sellerUuid');
    const thirdPartyUserKey = this.thirdPartyService.getThirdPartyUserKeyFromUrl();

    authReq = this.addCustomHeaders(authReq, {
      sellerUuid: sellerUuid || undefined,
      thirdPartyToken: thirdPartyUserKey || undefined
    });

    if (!this.noAuthAPI.some((url: string) => request.url.includes(url))) {
      let tokenDetails = JSON.parse(localStorage.getItem('tokenDetails') ?? 'null');
      if (!tokenDetails?.token) {
        this.configService.sessionLogout();
      } else {
        authReq = this.addWithCredentialsHeaderAndToken(authReq, tokenDetails.token);
      }
    } else if (request.url.includes('Authentication/RefreshToken') || request.url.includes('Authentication/AdminLogin')) {
      authReq = this.addWithCredentialsHeader(authReq);
    }

    return next.handle(authReq).pipe(
      catchError(err => {
        if (this.errorStatus.includes(err.status) && !(request.url.includes('Authentication/RefreshToken') || request.url.includes('Authentication/AdminLogin'))) {
          return this.handle401Error(authReq, next);
        }
        return throwError(() => err);
      }),
    );
  }

  private addCustomHeaders(
    request: HttpRequest<any>,
    options: { sellerUuid?: string; thirdPartyToken?: string; }): HttpRequest<any> {

    const customHeaders: Record<string, string> = {};

    if (options.sellerUuid) {
      customHeaders['X-Seller-Uuid'] = options.sellerUuid;
    }

    if (options.thirdPartyToken) {
      customHeaders['X-ThirdParty-Token'] = options.thirdPartyToken;
    }

    // Preserve existing headers
    const preservedHeaders = request.headers.keys().reduce((acc, key) => {
      const value = request.headers.get(key);
      if (value !== null) acc[key] = value;
      return acc;
    }, {} as Record<string, string>);

    return request.clone({
      setHeaders: {
        ...customHeaders,
        ...preservedHeaders
      }
    });
  }

  private addWithCredentialsHeader(request: HttpRequest<any>) {
    return request = request.clone({
      withCredentials: true
    });
  }

  private addWithCredentialsHeaderAndToken(request: HttpRequest<any>, token: string) {
    return request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        ...request.headers.keys().reduce((acc, key) => {
          if (key === 'Authorization') return acc;

          acc[key] = request.headers.get(key) as string;
          return acc;
        }, {} as Record<string, string>)
      },
      withCredentials: true
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.authorizedService.refreshToken().pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this.authorizedService.persistAccessToken(token);
          this.refreshTokenSubject.next(token.accessToken);
          return next.handle(this.addWithCredentialsHeaderAndToken(request, token.accessToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;

          //Important: make sure pending subscribers are released
          this.refreshTokenSubject.error(err);
          this.logglyService.log('Mobile-AuthInterceptor - Refresh token failed, redirecting to login page..');

          setTimeout(() => {
            this.configService.sessionLogout(true);
          }, 1000);
          return throwError(err);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addWithCredentialsHeaderAndToken(request, token)))
    );
  }
}
